import type { Agreements } from '../filterValues';
import { SEARCH_PARAMETERS_SCHEMA } from './data';
import { SearchParameterFieldKind } from './rows';

type EnumLike = Record<string, string>;
type EnumValues<E extends EnumLike> = E[keyof E];

type SimpleKindValueMap = {
  [SearchParameterFieldKind.STRING]: string;
  [SearchParameterFieldKind.STRING_ARRAY]: readonly string[];
  [SearchParameterFieldKind.AGREEMENTS]: Agreements;
  [SearchParameterFieldKind.NUMBER]: number;
};

/**
 * Resolves a schema row to the TypeScript value type accepted for that field.
 *
 * ENUM rows become a union of their enum values.
 * ENUM_ARRAY rows become an array of those enum values.
 * Simple field kinds are mapped through `SimpleKindValueMap`.
 *
 * Array kinds are `readonly`. A search hands the same list to every caller and
 * an unstated one is a shared frozen empty list, so a reader that could push
 * to it would be writing into searches it has never seen. Callers may still
 * pass a mutable array in — that direction is safe and stays assignable.
 */
type SearchParameterFieldValue<R> = R extends {
  kind: SearchParameterFieldKind.ENUM;
  enum: infer E extends EnumLike;
}
  ? EnumValues<E>
  : R extends {
      kind: SearchParameterFieldKind.ENUM_ARRAY;
      enum: infer E extends EnumLike;
    }
  ? readonly EnumValues<E>[]
  : R extends { kind: keyof SimpleKindValueMap }
  ? SimpleKindValueMap[R['kind']]
  : never;

type SchemaRow = (typeof SEARCH_PARAMETERS_SCHEMA)[number];

/**
 * Search parameter values keyed by `SEARCH_PARAMETERS_SCHEMA[].name`.
 *
 * Value types are derived from each schema row:
 * - STRING: `string`
 * - STRING_ARRAY: `string[]`
 * - AGREEMENTS: `Agreements`
 * - ENUM: one of the row's `enum` values
 * - ENUM_ARRAY: array of the row's `enum` values
 *
 * Every field is optional, including the ones carrying a `defaultValue`: these
 * are the values a search *states*, and a search states nothing until it says
 * so. A row's `defaultValue` is the value it takes when unstated, which the
 * class getters supply and the serializer omits — neither of which requires it
 * to sit in the values themselves.
 *
 * Materialising defaults here instead would make absence unrepresentable, and a
 * default would become indistinguishable from a choice for the seven parameters
 * that carry one.
 */
export type SearchParameterValues = {
  [R in SchemaRow as R['name']]?: SearchParameterFieldValue<R>;
};

/** Every parameter name the schema declares. */
export type SearchParameterName = keyof SearchParameterValues;

/**
 * Every parameter name the schema declares, as values.
 *
 * The public name vocabulary of SearchParameters: it is what lets another
 * concept ask which parameters exist without reaching for
 * SEARCH_PARAMETERS_SCHEMA, which stays private to this folder along with the
 * field kinds, the lookup, and serialization. See `availableSearchParametersFor`
 * in searchMode, the only consumer outside this concept.
 */
export const SEARCH_PARAMETER_NAMES: readonly SearchParameterName[] =
  SEARCH_PARAMETERS_SCHEMA.map((row) => row.name);

/**
 * The value each defaulting parameter takes when a search states nothing,
 * typed from the rows that declare one.
 *
 * Derived rather than restated so a row's `defaultValue` stays the only place
 * it is written. The class getters read this to answer an unstated parameter,
 * and the serializer compares against the same rows to drop it from the url —
 * a second spelling of either would let the url omit one value while the search
 * ran under another.
 */
type RowsWithDefault = Extract<SchemaRow, { defaultValue: unknown }>;

/**
 * Array rows default without declaring it: an unstated array parameter holds
 * none of its values, which is what the kind means rather than what a row
 * chooses. See `schemaDefaults` in ./lookup.
 */
type ArrayRow = Extract<
  SchemaRow,
  {
    kind:
      | SearchParameterFieldKind.ENUM_ARRAY
      | SearchParameterFieldKind.STRING_ARRAY;
  }
>;

export type SchemaDefaults = {
  [R in RowsWithDefault as R['name']]: SearchParameterFieldValue<R>;
} & {
  [R in ArrayRow as R['name']]: SearchParameterFieldValue<R>;
};

/**
 * The unit a parameter is toggled by: one element for array-valued
 * parameters, the whole value for everything else.
 *
 * SearchParameterValues is an intersection of two mapped types rather than a
 * single homomorphic one, so `NonNullable` — not a `-?` modifier — is what
 * strips the optionality off the rows without a default.
 */
export type SearchParameterElement<N extends SearchParameterName> = NonNullable<
  SearchParameterValues[N]
> extends readonly (infer Element)[]
  ? Element
  : NonNullable<SearchParameterValues[N]>;
